import React, { useState, useContext, useCallback } from 'react';

import { useHistory } from 'react-router-dom';

import IconButton from '@mui/material/IconButton';

import MiraDialog from '../Dialogs/MiraDialog';
import NewVersionDialog from '../Dialogs/NewVersionDialog';

import {
  PricingSheetState,
  PdfQuality,
} from '../../../../../shared/types';

import NotificationProvider from '../../../providers/Notification/NotificationProvider';
import RowsProvider from '../../../providers/Rows/RowsProvider';
import useUserContext from '../../../providers/User/UserProvider';

import DefaultTooltip from '../../../components/Tooltips/DefaultTooltip';

import { downloadPdf } from '../../../helpers/downloadPdf';
import { isEditingRights } from '../../../../../shared/helpers/isEditingRights';
import { isEditingLocked } from '../../../../../shared/helpers/isEditingLocked';
import RenewPricingSheetDialog from '../Dialogs/RenewPricingSheetDialog';
import {
  ActionMenu,
  ActionMenuItem,
} from '../../../components/Common/ActionMenu';
import {
  AltRouteOutlined,
  Autorenew,
  Edit,
  ImportExport,
  MoreVert,
  PictureAsPdf,
  RemoveRedEye,
} from '@mui/icons-material';
import { DataTableRowActionsContainer } from '../../../components/Common/DataTable/DataTableRowActions';

import ResponsiblePersonDialog from '../Dialogs/ResponsiblePersonDialog';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { hasSheetOwnerAccess } from '../../../../../shared/helpers/hasSheetOwnerAccess';

const PricingRow: React.FC<any> = ({
  item,
  infoToggle,
  updateSheets,
}) => {
  const pricingSheet = item;
  const { hierarchy } = useContext(RowsProvider);
  const { setNotification } = useContext(NotificationProvider);
  const { isAdmin, userEmail } = useUserContext();

  let history = useHistory();

  const [miraDialogOpen, setMiraDialogOpen] = React.useState(false);
  const [newSheetDialogOpen, setNewSheetDialogOpen] =
    React.useState(false);

  const [renewDialogOpen, setRenewDialogOpen] = React.useState(false);
  const [
    responsiblePersonDialogOpen,
    setResponsiblePersonDialogOpen,
  ] = React.useState(false);

  const handleMiraClickOpen = () => {
    setMiraDialogOpen(true);
  };

  const handleMiraDialogClose = () => {
    setMiraDialogOpen(false);
  };

  const handleNewSheetClickOpen = () => {
    setNewSheetDialogOpen(true);
  };
  const handleRenewDialogOpen = () => {
    setRenewDialogOpen(true);
  };

  const handleNewSheetDialogClose = () => {
    setNewSheetDialogOpen(false);
  };

  const handleRenewDialogClose = () => {
    setRenewDialogOpen(false);
  };

  const handleResponsiblePersonDialogClose = () => {
    setResponsiblePersonDialogOpen(false);
  };

  const isArchived = !!pricingSheet.archivedAt;

  const [pdfLoading, setPdfLoading] = useState(false);

  const goToEditPricingSheet = (pricingSheetId: string) => {
    history.push('/muokkaa-hinnastoa/' + pricingSheetId);
  };

  const editButtonTooltipText = () => {
    if (isEditingLocked(pricingSheet, userEmail ?? '')) {
      return `${pricingSheet.editorFullName} on muokkaamassa hinnastoa. Ainoastaan yksi henkilö voi muokata hinnastoa kerrallaan`;
    }
    if (pricingSheet.revision) {
      return 'Hinnastosta on tehty uusi versio. Ainoastaan viimeisin versio on muokattavissa';
    }
    if (!isEditDisabled()) {
      return 'Muokkaa';
    }
    return '';
  };

  const inspectButtonTooltipText = () => {
    return 'Tarkastele';
  };

  const isEditDisabled = () => {
    // Archived sheets can't be edited
    if (!!pricingSheet.archivedAt) {
      return true;
    }

    if (isEditingLocked(pricingSheet, userEmail ?? '')) {
      return true;
    }

    // User must have owner access or have editing rights to edit sheet
    if (
      !hasSheetOwnerAccess(pricingSheet, userEmail) &&
      !isEditingRights(pricingSheet.editingRights, userEmail)
    ) {
      return true;
    }

    // Sheet edit is disabled after a new version is created.
    // Only latest version can be edited
    if (pricingSheet.revision) {
      return true;
    }

    // Sheet cant't be edited after it's ready or in use
    if (
      pricingSheet.state === PricingSheetState.Ready ||
      pricingSheet.state === PricingSheetState.InUse ||
      pricingSheet.state === PricingSheetState.Approved ||
      pricingSheet.state === PricingSheetState.Pending
    ) {
      return true;
    }

    // Edit is disabled if another user is editing it
    if (pricingSheet.isSharedSheetLocked) {
      return true;
    }
    return false;
  };

  const onClickInspectPricingSheet = () => {
    window.open(`/tarkastele-hinnastoa/${pricingSheet.id}`, '_blank');
  };

  // export to mira action
  const miraExportAction = useCallback(
    (closeMenu: () => void) => {
      const miraExportTooltipTitle = () => {
        if (
          !hasSheetOwnerAccess(
            {
              userEmail: pricingSheet.userEmail,
              responsiblePerson: pricingSheet.responsiblePerson,
            },
            userEmail,
          ) &&
          pricingSheet.userEmail !== 'admin' &&
          !isAdmin &&
          !isEditingRights(pricingSheet.editingRights, userEmail)
        ) {
          return 'Vain omia tai sinulle jaettuja hinnastoja voi viedä miraan';
        }
        if (
          pricingSheet.state !== PricingSheetState.Approved &&
          pricingSheet.state !== PricingSheetState.Ready
        ) {
          return 'Hinnaston täytyy olla hyväksytty ennen vientiä';
        }
        return 'Hinnasto on valmis Miraan, voit pyytää sille vientiä';
      };

      const isMiraExportDisabled = () => {
        // Archived sheets can't be exported
        if (isArchived) {
          return true;
        }
        // Sheet to export must be Approved or Ready
        if (
          !(pricingSheet.state === PricingSheetState.Approved) &&
          !(pricingSheet.state === PricingSheetState.Ready)
        ) {
          return true;
        }
        // User must have owner access or have editing rights to export the sheet
        if (
          !hasSheetOwnerAccess(
            {
              userEmail: pricingSheet.userEmail,
              responsiblePerson: pricingSheet.responsiblePerson,
            },
            userEmail,
          ) &&
          !isEditingRights(pricingSheet.editingRights, userEmail)
        ) {
          return true;
        }
        return false;
      };

      return (
        <DefaultTooltip
          title={miraExportTooltipTitle()}
          placement={'top'}
          enterDelay={500}
          key={'export-to-mira-tooltip'}
        >
          <ActionMenuItem
            key={'export-to-mira'}
            disabled={isMiraExportDisabled()}
            icon={ImportExport}
            label={'Vie miraan'}
            onClick={() => {
              handleMiraClickOpen();
              closeMenu();
            }}
          />
        </DefaultTooltip>
      );
    },
    [
      isAdmin,
      isArchived,
      pricingSheet.editingRights,
      pricingSheet.userEmail,
      pricingSheet.state,
      userEmail,
      pricingSheet.responsiblePerson,
    ],
  );

  // renew pricing sheet action
  const renewSheetAction = useCallback(
    (closeMenu: () => void) => {
      const renewTooltip = () => {
        if (
          !hasSheetOwnerAccess(
            {
              userEmail: pricingSheet.userEmail,
              responsiblePerson: pricingSheet.responsiblePerson,
            },
            userEmail,
          ) &&
          !isEditingRights(pricingSheet.editingRights, userEmail)
        ) {
          return 'Vain omien tai sinulle jaettujen hinnastojen voimassaoloa voi jatkaa';
        }

        if (pricingSheet.revision) {
          return 'Hinnastosta on tehty uusi versio. Ainoastaan viimeisimmän hinnaston voimassaoloa voi jatkaa';
        }

        return '';
      };
      const isRenewDisabled = () => {
        // There is a new version of the sheet
        if (pricingSheet.revision) {
          return true;
        }

        // User must be the creator or have editing rights to edit sheet
        if (
          !hasSheetOwnerAccess(
            {
              userEmail: pricingSheet.userEmail,
              responsiblePerson: pricingSheet.responsiblePerson,
            },
            userEmail,
          ) &&
          !isEditingRights(pricingSheet.editingRights, userEmail)
        ) {
          return true;
        }

        return !(pricingSheet.state === PricingSheetState.InUse);
      };

      return (
        <DefaultTooltip
          title={renewTooltip()}
          placement={'top'}
          enterDelay={500}
          key={'renew-sheet-tooltip'}
        >
          <ActionMenuItem
            key={'renew-sheet'}
            disabled={isRenewDisabled()}
            icon={Autorenew}
            label={'Jatka voimassaoloa'}
            onClick={() => {
              handleRenewDialogOpen();
              closeMenu();
            }}
          />
        </DefaultTooltip>
      );
    },
    [
      pricingSheet.editingRights,
      pricingSheet.revision,
      pricingSheet.userEmail,
      pricingSheet.state,
      userEmail,
      pricingSheet.responsiblePerson,
    ],
  );

  // create new version sheet action
  const newVersionAction = useCallback(
    (closeMenu: () => void) => {
      const newVersionTooltip = () => {
        if (
          !hasSheetOwnerAccess(
            {
              userEmail: pricingSheet.userEmail,
              responsiblePerson: pricingSheet.responsiblePerson,
            },
            userEmail,
          ) &&
          !isEditingRights(pricingSheet.editingRights, userEmail)
        ) {
          return 'Vain omista tai sinulle jaetuista hinnastoista voi tehdä uuden version';
        }

        if (pricingSheet.revision) {
          return 'Hinnastosta on tehty uusi versio. Ainoastaan viimeisimmästä hinnastosta voi luoda uuden version';
        }

        return '';
      };

      const isNewVersionDisabled = () => {
        // There is a new version of the sheet
        if (pricingSheet.revision) {
          return true;
        }

        // User must be the creator or have editing rights to edit sheet
        if (
          !hasSheetOwnerAccess(
            {
              userEmail: pricingSheet.userEmail,
              responsiblePerson: pricingSheet.responsiblePerson,
            },
            userEmail,
          ) &&
          !isEditingRights(pricingSheet.editingRights, userEmail)
        ) {
          return true;
        }

        return !(
          pricingSheet.state === PricingSheetState.InUse ||
          pricingSheet.state === PricingSheetState.Approved ||
          pricingSheet.state === PricingSheetState.Pending
        );
      };

      return (
        <DefaultTooltip
          title={newVersionTooltip()}
          placement={'top'}
          enterDelay={500}
          key={'new-version-sheet-tooltip'}
        >
          <ActionMenuItem
            key={'new-version-sheet'}
            disabled={isNewVersionDisabled()}
            icon={AltRouteOutlined}
            label={'Luo uusi versio'}
            onClick={() => {
              handleNewSheetClickOpen();
              closeMenu();
            }}
          />
        </DefaultTooltip>
      );
    },
    [
      pricingSheet.editingRights,
      pricingSheet.revision,
      pricingSheet.userEmail,
      pricingSheet.state,
      pricingSheet.responsiblePerson,
      userEmail,
    ],
  );

  // renew pricing sheet action
  const downloadPdfAction = useCallback(
    (closeMenu: () => void) => {
      const getPricingSheetPdf = async (
        id: string,
        name: string,
        quality: PdfQuality,
      ) => {
        try {
          setPdfLoading(true);
          await downloadPdf(id, name, quality);
        } catch (error) {
          setNotification({
            type: 'SNACKBAR',
            duration: 4000,
            severity: 'error',
            message: 'Hinnaston lataaminen epäonnistui',
          });
        } finally {
          setPdfLoading(false);
          closeMenu();
        }
      };

      return (
        <ActionMenuItem
          key={'download-pdf'}
          loading={pdfLoading}
          disabled={
            pdfLoading ||
            pricingSheet?.state === PricingSheetState.Pending ||
            pricingSheet?.state === PricingSheetState.WarningDraft ||
            pricingSheet?.state === PricingSheetState.OkDraft ||
            pricingSheet.userEmail === 'admin'
          }
          icon={PictureAsPdf}
          label={'Lataa PDF'}
          onClick={() => {
            getPricingSheetPdf(
              pricingSheet.id ?? '',
              pricingSheet.name,
              PdfQuality.LOW,
            );
          }}
        />
      );
    },
    [
      setNotification,
      pricingSheet.id,
      pricingSheet.name,
      pricingSheet.userEmail,
      pricingSheet?.state,
      pdfLoading,
    ],
  );

  // Transfer responsibility
  const responsiblePersonAction = useCallback(
    (closeMenu: () => void) => {
      return (
        <ActionMenuItem
          disabled={!isAdmin}
          key={'set-responsible-person'}
          icon={PersonAddAlt1Icon}
          label={'Aseta vastuuhenkilö'}
          onClick={() => {
            setResponsiblePersonDialogOpen(true);
            closeMenu();
          }}
        />
      );
    },
    [isAdmin],
  );

  return (
    <>
      <MiraDialog
        pricingSheet={pricingSheet}
        open={miraDialogOpen}
        updateSheets={updateSheets}
        title={'Valmis Miraan'}
        onClose={handleMiraDialogClose}
      />
      <RenewPricingSheetDialog
        pricingSheet={pricingSheet}
        open={renewDialogOpen}
        title={'Jatka hinnaston voimassaoloaikaa'}
        onClose={handleRenewDialogClose}
        hierarchy={hierarchy}
        goToEditPricingSheet={goToEditPricingSheet}
      />
      <NewVersionDialog
        pricingSheet={pricingSheet}
        open={newSheetDialogOpen}
        title={'Luo hinnastosta uusi versio'}
        onClose={handleNewSheetDialogClose}
        hierarchy={hierarchy}
        goToEditPricingSheet={goToEditPricingSheet}
      />
      <ResponsiblePersonDialog
        updateSheets={updateSheets}
        pricingSheet={pricingSheet}
        open={responsiblePersonDialogOpen}
        title={'Aseta hinnastolle uusi vastuuhenkilö'}
        onClose={handleResponsiblePersonDialogClose}
      />
      <DataTableRowActionsContainer
        sx={{
          justifyContent: 'end',
          px: 1,
        }}
      >
        <DefaultTooltip
          title={editButtonTooltipText()}
          placement={'top'}
        >
          <IconButton
            size={'small'}
            color={'primary'}
            disabled={isEditDisabled()}
            onClick={() => goToEditPricingSheet(pricingSheet.id)}
            sx={{ fontSize: '1.25rem', p: '7px' }}
          >
            <Edit fontSize={'inherit'} />
          </IconButton>
        </DefaultTooltip>
        <DefaultTooltip
          title={inspectButtonTooltipText()}
          placement={'top'}
        >
          <IconButton
            size={'small'}
            color={'primary'}
            sx={{ fontSize: '1.25rem', p: '7px' }}
            onClick={() => {
              onClickInspectPricingSheet();
            }}
          >
            <RemoveRedEye fontSize={'inherit'} />
          </IconButton>
        </DefaultTooltip>
        <ActionMenu
          toggleIcon={<MoreVert />}
          renderItems={(closeMenu) => {
            return [
              miraExportAction(closeMenu),
              newVersionAction(closeMenu),
              renewSheetAction(closeMenu),
              responsiblePersonAction(closeMenu),
              downloadPdfAction(closeMenu),
            ];
          }}
        />
        {infoToggle}
      </DataTableRowActionsContainer>
    </>
  );
};

export default PricingRow;
